// Smooth switch between the big centred chat and the small corner window.
//
// The two layouts sit in different places with !important rules (styles/chatbot.css), so the
// window itself can't be tweened. Instead a stand-in card (a copy of the window as it was)
// flies from the old frame to the new one, its contents fading as the frame changes, and then
// the real window fades in at its new size. Nothing is squashed: the copy is cropped, never scaled.

const EASE = 'cubic-bezier(0.22, 0.9, 0.24, 1)';

const reduced = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Where the window is now (call before the layout changes). */
export const captureWindow = (el) => {
  if (!el || reduced() || typeof el.animate !== 'function') return null;
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const copy = el.cloneNode(true);
  copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
  return { rect, radius: style.borderRadius, background: style.backgroundColor, copy };
};

/** Fly from the captured frame to where the window is now (call right after the layout changes). */
export const morphWindow = (el, from) => {
  if (!el || !from) return;
  const to = el.getBoundingClientRect();
  if (Math.abs(to.width - from.rect.width) < 2 && Math.abs(to.height - from.rect.height) < 2) return;
  const toRadius = window.getComputedStyle(el).borderRadius;

  const ghost = document.createElement('div');
  ghost.className = 'cb-morph';
  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${from.rect.left}px`,
    top: `${from.rect.top}px`,
    width: `${from.rect.width}px`,
    height: `${from.rect.height}px`,
    borderRadius: from.radius,
    background: from.background || '#faf6ec',
    overflow: 'hidden',
    zIndex: 1200,
    pointerEvents: 'none',
    boxShadow: '0 24px 64px rgba(59, 44, 5, 0.22), 0 2px 6px rgba(59, 44, 5, 0.08)',
  });
  // The old contents, pinned to the top-left at their old size, so the frame crops them
  const copy = from.copy;
  Object.assign(copy.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    right: 'auto',
    bottom: 'auto',
    width: `${from.rect.width}px`,
    height: `${from.rect.height}px`,
    maxWidth: 'none',
    maxHeight: 'none',
    minWidth: '0',
    minHeight: '0',
    margin: '0',
    transform: 'none',
    boxShadow: 'none',
  });
  copy.style.setProperty('position', 'absolute', 'important');
  copy.style.setProperty('transform', 'none', 'important');
  copy.style.setProperty('top', '0', 'important');
  copy.style.setProperty('left', '0', 'important');
  copy.style.setProperty('right', 'auto', 'important');
  copy.style.setProperty('bottom', 'auto', 'important');
  copy.style.setProperty('width', `${from.rect.width}px`, 'important');
  copy.style.setProperty('height', `${from.rect.height}px`, 'important');
  copy.style.setProperty('max-width', 'none', 'important');
  copy.style.setProperty('max-height', 'none', 'important');
  copy.style.setProperty('min-width', '0', 'important');
  copy.style.setProperty('min-height', '0', 'important');
  ghost.appendChild(copy);
  document.body.appendChild(ghost);

  // Hide the real window until the stand-in lands
  el.style.setProperty('opacity', '0');

  const duration = 420;
  const flight = ghost.animate(
    [
      { left: `${from.rect.left}px`, top: `${from.rect.top}px`, width: `${from.rect.width}px`, height: `${from.rect.height}px`, borderRadius: from.radius },
      { left: `${to.left}px`, top: `${to.top}px`, width: `${to.width}px`, height: `${to.height}px`, borderRadius: toRadius },
    ],
    { duration, easing: EASE, fill: 'forwards' },
  );
  copy.animate([{ opacity: 1 }, { opacity: 0.15, offset: 0.55 }, { opacity: 0 }], { duration, easing: 'ease-out', fill: 'forwards' });

  const done = () => {
    el.style.removeProperty('opacity');
    el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
    const fade = ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-out', fill: 'forwards' });
    fade.onfinish = () => ghost.remove();
    fade.oncancel = () => ghost.remove();
  };
  flight.onfinish = done;
  flight.oncancel = done;
};
